import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send the harmony_refresh cookie on cross-origin calls to the backend. This
// is what lets POST /api/auth/refresh see the HttpOnly refresh cookie set
// at login. The cookie itself is Path=/api/auth so it only rides on auth
// calls — sub-app API calls (/api/timesheet/*, /api/pms/*, etc.) still
// carry the bearer access token and NOT the cookie. Set once at module
// load so every axios instance the app uses inherits the default.
axios.defaults.withCredentials = true;

// Silent-refresh cadence. 12 minutes stays comfortably ahead of the 15-min
// access-token TTL that Phase A cuts over to once the frontend migration
// is verified in preview. While access tokens are still 24h during the
// grace period this feels aggressive but is harmless — the backend rotates
// the refresh token on real calls only and treats duplicates in a 60s
// window as idempotent replays (see rotate_session in server.py).
const AUTH_REFRESH_INTERVAL_MS = 12 * 60 * 1000;

// A token with less than this much life left is treated as already dead —
// we refresh before spending a request to discover it 401s. The 12-minute
// timer above only fires while the machine is awake: setInterval does not
// run during sleep, so after any suspend the in-hand token can be hours
// stale. Every path that is about to USE a token checks this first.
const NEAR_EXPIRY_MS = 60 * 1000;

// Read `exp` out of a JWT without verifying it. Only ever used to decide
// whether to pre-emptively refresh — the server remains the authority on
// whether a token is actually good. Mirrors decodeJwt in lib/useAuthRefresh.js.
const decodeJwt = (jwt) => {
  try {
    const [, payload] = (jwt || '').split('.');
    if (!payload) return null;
    const base = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base + '='.repeat((4 - base.length % 4) % 4)));
  } catch (_) { return null; }
};

// True when the token is missing, unparseable, expired, or about to be.
// Unparseable counts as stale on purpose: refreshing costs one request,
// while trusting it costs the session.
const isTokenStale = (jwt, withinMs = NEAR_EXPIRY_MS) => {
  const claims = decodeJwt(jwt);
  if (!claims?.exp) return true;
  return claims.exp * 1000 - Date.now() < withinMs;
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState(null);
  const [originalToken, setOriginalToken] = useState(null);

  // Configure axios defaults when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Drop this tab's copy of the session WITHOUT telling the server.
  //
  // This is the difference between "I can't use this session right now"
  // and "destroy this session". Only a deliberate Sign out should do the
  // latter: POST /api/auth/logout needs no access token — the refresh
  // cookie alone is the credential — so it happily revokes a session row
  // with 29 days left and deletes the cookie, after which /auth/refresh
  // returns 401 "Session no longer valid" forever. Every AUTOMATIC path
  // (a 401 from an expired access token, a refresh that failed) must land
  // here instead, so the 30-day cookie survives to be retried.
  const clearLocalSession = useCallback(() => {
    setToken(null);
    setUser(null);
    setError(null);
    setIsImpersonating(false);
    setOriginalUser(null);
    setOriginalToken(null);
    sessionStorage.removeItem('harmony_token');
    sessionStorage.removeItem('harmony_user');
    sessionStorage.removeItem('harmony_impersonating');
    sessionStorage.removeItem('harmony_original_user');
    sessionStorage.removeItem('harmony_original_token');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Single-flight refresh shared by the timer, the wake handler, the 401
  // interceptor and the mount bootstrap, so a laptop waking up with four
  // triggers at once sends one request instead of four racing rotations.
  const refreshInFlight = useRef(null);
  const runRefresh = useCallback(async () => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const p = (async () => {
      try {
        const r = await axios.post(`${API}/auth/refresh`, {});
        const newToken = r.data?.token;
        if (!newToken) return null;
        setToken(newToken);
        sessionStorage.setItem('harmony_token', newToken);
        return newToken;
      } finally {
        refreshInFlight.current = null;
      }
    })();
    refreshInFlight.current = p;
    return p;
  }, []);

  // Check for existing session on mount.
  //
  // sessionStorage is per-tab and is destroyed when the tab closes, so it
  // only ever restores a reload or an in-tab navigation. The durable
  // credential is the HttpOnly `harmony_refresh` cookie, which lives for
  // REFRESH_TOKEN_TTL_DAYS (30) — previously we never consulted it here, so
  // closing the browser dropped the user back to the login screen even
  // though the server still considered them signed in.
  //
  // Now: no in-tab token → try one silent refresh. The cookie proves the
  // session, /auth/me rehydrates the user record (including
  // is_temporary_password, so the forced-password-change guard still
  // fires). A 401 means genuinely signed out and we fall through to the
  // login screen exactly as before.
  //
  // The access token stays in sessionStorage on purpose. It expires in 15
  // minutes and the long-lived credential remains HttpOnly, so nothing
  // JavaScript can read outlives the tab.
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const storedToken = sessionStorage.getItem('harmony_token');
      const storedUser = sessionStorage.getItem('harmony_user');
      const storedImpersonating = sessionStorage.getItem('harmony_impersonating');
      const storedOriginalUser = sessionStorage.getItem('harmony_original_user');
      const storedOriginalToken = sessionStorage.getItem('harmony_original_token');

      if (storedToken && storedUser) {
        const impersonating = storedImpersonating === 'true' && storedOriginalUser && storedOriginalToken;

        // A stored token that has already expired must NOT be adopted.
        // sessionStorage survives a reload and a browser session-restore,
        // so this is exactly the overnight case: the tab comes back with
        // last night's dead token, Dashboard immediately fires
        // GET /api/subapps with it, and that 401 used to revoke a session
        // that still had weeks left. Refresh first, then render.
        //
        // Impersonation is exempt: those are their own 1h JWT and the
        // backend explicitly refuses to extend them through /auth/refresh.
        if (!impersonating && isTokenStale(storedToken)) {
          try {
            const fresh = await runRefresh();
            if (cancelled) return;
            if (fresh) {
              setUser(JSON.parse(storedUser));
              setLoading(false);
              return;
            }
          } catch (_) { /* fall through to the cookie bootstrap below */ }
          // Refresh failed. Drop the dead token locally and let the cookie
          // path below have a go; if that fails too we land on /login with
          // the session left intact server-side.
          if (cancelled) return;
          sessionStorage.removeItem('harmony_token');
          sessionStorage.removeItem('harmony_user');
        } else {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));

          if (impersonating) {
            setIsImpersonating(true);
            setOriginalUser(JSON.parse(storedOriginalUser));
            setOriginalToken(storedOriginalToken);
          }
          setLoading(false);
          return;
        }
      }

      try {
        const r = await axios.post(`${API}/auth/refresh`, {});
        const newToken = r.data?.token;
        if (!newToken) throw new Error('refresh returned no token');

        // Explicit header — axios.defaults hasn't been populated yet on a
        // cold mount, and the token-sync effect won't have run.
        const me = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${newToken}` },
        });
        if (cancelled) return;

        setToken(newToken);
        setUser(me.data);
        sessionStorage.setItem('harmony_token', newToken);
        sessionStorage.setItem('harmony_user', JSON.stringify(me.data));
      } catch (_) {
        // No usable refresh cookie — stay signed out. Not an error worth
        // surfacing: this is the normal path for a first-time visitor.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();
    return () => { cancelled = true; };
    // Mount-only by design. runRefresh is stable (useCallback with no deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Silent refresh — every 12 minutes while we have a token, hit
  // /api/auth/refresh. The backend reads the harmony_refresh HttpOnly
  // cookie (delivered by axios.defaults.withCredentials above), rotates
  // the refresh JTI, and returns a new access token that we drop into
  // state + sessionStorage. Failure paths:
  //   401 → the refresh COOKIE was rejected, so the session really is
  //         dead (revoked, expired, deactivated). Clear locally and let
  //         the next route guard bounce the user to /login. We do not
  //         call logout() — the server has already decided, and telling
  //         it to revoke would only destroy a session it chose to keep.
  //   network error → non-fatal. The next tick will try again, and the
  //                   401 interceptor recovers any request that raced it.
  // This timer only runs while the machine is awake; the wake handler
  // below covers the suspend case.
  // We skip refresh while impersonating — impersonation tokens are their
  // own 1h JWT and MUST NOT be extended through the normal refresh path
  // (the backend rejects that explicitly). Users stop-impersonating to
  // return to their own session.
  useEffect(() => {
    if (!token || isImpersonating) return;
    const doRefresh = async () => {
      try {
        await runRefresh();
      } catch (err) {
        if (err?.response?.status === 401) {
          // The refresh cookie itself was rejected — revoked, expired or
          // the user was deactivated. Clear locally only; the server has
          // already made its decision and calling /auth/logout here would
          // just be us destroying whatever it decided to keep.
          clearLocalSession();
        }
        // Other errors (offline, 5xx, proxy blip): non-fatal, next tick retries.
      }
    };
    const timerId = setInterval(doRefresh, AUTH_REFRESH_INTERVAL_MS);
    return () => clearInterval(timerId);
    // token is the trigger; isImpersonating gates whether the interval
    // runs at all. runRefresh and clearLocalSession are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isImpersonating]);

  // Wake-up recovery. setInterval does not fire while the machine is
  // suspended, so after a sleep the 12-minute timer is overdue and the
  // token in hand is already expired. Without this, the first request
  // after waking 401s. Re-check whenever the tab becomes visible, regains
  // focus, or the network comes back, and refresh if the token is stale.
  useEffect(() => {
    if (!token || isImpersonating) return;
    const recheck = () => {
      if (document.visibilityState === 'hidden') return;
      const current = sessionStorage.getItem('harmony_token');
      if (current && isTokenStale(current)) {
        runRefresh().catch((err) => {
          if (err?.response?.status === 401) clearLocalSession();
        });
      }
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);
    window.addEventListener('online', recheck);
    recheck();
    return () => {
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
      window.removeEventListener('online', recheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isImpersonating]);

  // Global 401 interceptor for the SHELL. A 401 here is overwhelmingly an
  // EXPIRED ACCESS TOKEN, not a dead session — access tokens live 15
  // minutes while the session lives 30 days. So we do what the sub-apps
  // already do in lib/useAuthRefresh.js: refresh once and replay the
  // request. Only if the replay also fails do we clear local state, and
  // even then we never POST /auth/logout, because that would revoke a
  // session the server was still perfectly happy with.
  //
  // This is the fix for the daily logout: previously any single 401 —
  // typically the first request after an overnight sleep — called the
  // full logout() and permanently destroyed a session with weeks left.
  //
  // Excluded: /auth/refresh (the timer owns its own 401 path),
  // /auth/login (a 401 there is just a wrong password), and — importantly —
  // any request that is NOT the shell's own.
  //
  // Why that last exclusion matters. This interceptor is registered on the
  // shared module-level axios instance, so it sees 401s belonging to the
  // sub-apps too. Those already run their own recovery
  // (lib/useAuthRefresh.js, TimesheetContext.jsx) and, critically, they
  // install a REQUEST interceptor that unconditionally re-stamps
  // Authorization from their private tokenRef. Replaying such a request
  // from here is futile: axios(cfg) re-runs the request interceptors, the
  // sub-app overwrites our fresh token with the same expired one that just
  // 401'd, the replay fails, and we would then wrongly clear the shell
  // session. So we identify ownership by the token that was actually sent
  // and leave anything else to whoever owns it. Checking the token rather
  // than a list of URL prefixes keeps this correct as sub-apps are added.
  useEffect(() => {
    const authHeader = (cfg) => {
      const h = cfg?.headers;
      if (!h) return '';
      if (typeof h.get === 'function') return h.get('Authorization') || '';
      return h.Authorization || h.authorization || '';
    };

    const id = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const url = error?.config?.url || '';
        const cfg = error?.config || {};
        const shellToken = sessionStorage.getItem('harmony_token');
        const sent = authHeader(cfg);
        // No Authorization at all → a plain shell call riding the axios
        // defaults. Otherwise it must match the shell's own token.
        const isOurs = !sent || sent === `Bearer ${shellToken}`;

        const recoverable =
          error?.response?.status === 401 &&
          !url.endsWith('/auth/refresh') &&
          !url.endsWith('/auth/login') &&
          !!shellToken &&
          isOurs;

        if (!recoverable || cfg._authRetried) {
          // Second failure on the same request means refreshing did not
          // help — the session really is gone. Clear locally; the route
          // guards bounce to /login.
          if (recoverable && cfg._authRetried) clearLocalSession();
          return Promise.reject(error);
        }

        // Impersonation tokens can't be refreshed (backend refuses), so a
        // 401 while impersonating is terminal for that token. The
        // impersonation banner in Layout.jsx keeps "Exit Impersonation"
        // on screen throughout, so this is a visible dead end, not a
        // silent one.
        if (sessionStorage.getItem('harmony_impersonating') === 'true') {
          return Promise.reject(error);
        }

        try {
          const fresh = await runRefresh();
          if (fresh) {
            cfg._authRetried = true;
            // Write through AxiosHeaders' own setter when present —
            // spreading an AxiosHeaders instance drops its prototype and
            // the header can be lost on the replay.
            if (cfg.headers && typeof cfg.headers.set === 'function') {
              cfg.headers.set('Authorization', `Bearer ${fresh}`);
            } else {
              cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${fresh}` };
            }
            return axios(cfg);
          }
          clearLocalSession();
        } catch (_) {
          clearLocalSession();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (username, password) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const { token: newToken, user: userData, requires_2fa, requires_2fa_setup, temp_token } = response.data;
      
      // 2FA required — don't set session, return the state
      if (requires_2fa || requires_2fa_setup) {
        return { 
          success: true, 
          user: userData, 
          requires_2fa, 
          requires_2fa_setup, 
          temp_token 
        };
      }
      
      setToken(newToken);
      setUser(userData);
      
      // Store in sessionStorage for persistence across page refreshes
      sessionStorage.setItem('harmony_token', newToken);
      sessionStorage.setItem('harmony_user', JSON.stringify(userData));
      
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    // Best-effort server-side revoke: /api/auth/logout reads the
    // harmony_refresh cookie (which withCredentials + our defaults deliver
    // automatically) and revokes that specific session row. Doesn't need
    // an access token — the cookie is the credential. Wrapped in a
    // try/catch because we ALWAYS want to clear local state even if the
    // network call fails.
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_) { /* fall through — clear locally regardless */ }
    clearLocalSession();
    // NOTE: this is the DESTRUCTIVE path and belongs to the Sign out
    // button alone (components/Layout.jsx). Automatic recovery paths must
    // call clearLocalSession() instead — see the note on that function.
  }, [clearLocalSession]);

  const complete2FALogin = useCallback((newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    sessionStorage.setItem('harmony_token', newToken);
    sessionStorage.setItem('harmony_user', JSON.stringify(userData));
  }, []);

  // Start impersonating a user
  const startImpersonation = useCallback(async (userId) => {
    try {
      const response = await axios.post(`${API}/auth/impersonate/${userId}`);
      const { token: impToken, user: impUser, original_token: origToken, original_user: origUser } = response.data;
      
      // Set impersonation state
      setToken(impToken);
      setUser(impUser);
      setIsImpersonating(true);
      setOriginalUser(origUser);
      setOriginalToken(origToken);
      
      // Persist to sessionStorage
      sessionStorage.setItem('harmony_token', impToken);
      sessionStorage.setItem('harmony_user', JSON.stringify(impUser));
      sessionStorage.setItem('harmony_impersonating', 'true');
      sessionStorage.setItem('harmony_original_user', JSON.stringify(origUser));
      sessionStorage.setItem('harmony_original_token', origToken);
      
      return { success: true, user: impUser };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to start impersonation';
      return { success: false, error: message };
    }
  }, []);

  // Stop impersonation and restore original admin session
  const stopImpersonation = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/auth/stop-impersonation`);
      const { token: adminToken, user: adminUser } = response.data;
      
      setToken(adminToken);
      setUser(adminUser);
      setIsImpersonating(false);
      setOriginalUser(null);
      setOriginalToken(null);
      
      sessionStorage.setItem('harmony_token', adminToken);
      sessionStorage.setItem('harmony_user', JSON.stringify(adminUser));
      sessionStorage.removeItem('harmony_impersonating');
      sessionStorage.removeItem('harmony_original_user');
      sessionStorage.removeItem('harmony_original_token');
      
      return { success: true };
    } catch (err) {
      // Fallback: restore from stored original token
      if (originalToken && originalUser) {
        setToken(originalToken);
        setUser(originalUser);
        setIsImpersonating(false);
        setOriginalUser(null);
        setOriginalToken(null);
        
        sessionStorage.setItem('harmony_token', originalToken);
        sessionStorage.setItem('harmony_user', JSON.stringify(originalUser));
        sessionStorage.removeItem('harmony_impersonating');
        sessionStorage.removeItem('harmony_original_user');
        sessionStorage.removeItem('harmony_original_token');
        
        return { success: true };
      }
      const message = err.response?.data?.detail || 'Failed to stop impersonation';
      return { success: false, error: message };
    }
  }, [originalToken, originalUser]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      sessionStorage.setItem('harmony_user', JSON.stringify(response.data));
    } catch (err) {
      // A 401 here has already been through the interceptor's
      // refresh-and-replay, so the session is genuinely unusable. Clear
      // locally — never POST /auth/logout on a machine-triggered failure.
      if (err.response?.status === 401) {
        clearLocalSession();
      }
    }
  }, [token, clearLocalSession]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      // Refresh user to update is_temporary_password flag
      await refreshUser();
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Password change failed';
      return { success: false, error: message };
    }
  }, [refreshUser]);

  // For first-time password change (temporary password users)
  const setNewPassword = useCallback(async (newPassword) => {
    try {
      await axios.post(`${API}/auth/set-new-password`, {
        new_password: newPassword
      });
      
      // Refresh user to update is_temporary_password flag
      await refreshUser();
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to set new password';
      return { success: false, error: message };
    }
  }, [refreshUser]);

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!token && !!user,
    isSuperAdmin: isImpersonating ? originalUser?.role === 'super_admin' : user?.role === 'super_admin',
    requiresPasswordChange: user?.is_temporary_password === true,
    isImpersonating,
    originalUser,
    login,
    logout,
    complete2FALogin,
    startImpersonation,
    stopImpersonation,
    refreshUser,
    changePassword,
    setNewPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
